/**
 * Video Analysis Service
 * Handles integration with Google Cloud Speech-to-Text for video transcription
 * and AI evaluation for video analysis
 */

import firebase from "firebase/app";
import "firebase/storage";

const ANALYSIS_API_BASE_URL = process.env.REACT_APP_ANALYSIS_API_URL || 'http://localhost:3001';

class VideoAnalysisService {
  /**
   * Submit video for analysis
   * @param {string} videoUrlOrPath - Full video URL or relative storage path
   * @param {string} candidateId - Candidate identifier
   * @param {string} examCode - Exam code
   * @param {string} selectedScenario - Selected scenario for context
   * @param {number} maxScore - Maximum score for the question
   * @returns {Promise<Object>} Analysis result
   */
  static async analyzeVideo(videoUrlOrPath, candidateId, examCode, selectedScenario = null, maxScore = 10) {
    try {
      // Step 1: Transcribe video using Google Speech-to-Text (exact text without spell correction)
      console.log('🎤 Transcribing video with Google Speech-to-Text...');
      const transcriptionResult = await this.transcribeVideo(videoUrlOrPath);
      
      if (!transcriptionResult.success || !transcriptionResult.transcript) {
        // If transcription fails, return error result
        return {
          success: false,
          error: transcriptionResult.error || 'Failed to transcribe video',
          transcript: '',
          finalScore: 0,
          fluencyScore: 0,
          grammarScore: 0,
          pronunciationScore: 0,
          mtiScore: 0,
          remark: 'Video transcription failed. Your video has been recorded but will be reviewed manually.',
          processingTime: 0,
          selectedScenario: selectedScenario || '',
          speechMetrics: {},
          grammarAnalysis: {},
          pronunciationAnalysis: {},
          mtiAnalysis: {},
          toneAnalysis: {},
          repetitionAnalysis: {}
        };
      }
      
      // Step 2: Get exact transcript without any modifications
      const exactTranscript = transcriptionResult.transcript;
      console.log('✅ Transcription completed:', exactTranscript.substring(0, 100) + '...');

      // Step 3: Return transcription results
      // The exact transcript (without spell correction) will be passed to AI evaluation
      // AI evaluation happens later in AIEvaluationService.evaluateSubmission
      return {
        success: true,
        transcript: exactTranscript, // Exact text without spell correction - passed to AI evaluation as-is
        confidence: transcriptionResult.confidence || 0,
        wordCount: transcriptionResult.wordCount || 0,
        words: transcriptionResult.words || [],
        languageCode: transcriptionResult.languageCode || 'en-US',
        selectedScenario: selectedScenario || '',
        timestamp: transcriptionResult.timestamp || new Date().toISOString(),
        // Note: AI evaluation scores will be calculated later in AIEvaluationService
        // using the exact transcript without spell correction
        fluencyScore: 0, // Will be calculated in AI evaluation
        grammarScore: 0, // Will be calculated in AI evaluation
        pronunciationScore: 0, // Will be calculated in AI evaluation
        mtiScore: 0, // Will be calculated in AI evaluation
        finalScore: 0, // Will be calculated in AI evaluation
        remark: 'Transcription completed. AI evaluation pending.',
        processingTime: 0,
        speechMetrics: {},
        grammarAnalysis: {},
        pronunciationAnalysis: {},
        mtiAnalysis: {},
        toneAnalysis: {},
        repetitionAnalysis: {}
      };
    } catch (error) {
      // Handle errors gracefully - don't fail the exam
      console.error('Video analysis error:', error);
      return {
        success: false,
        error: error.message || 'Video analysis service temporarily unavailable',
        transcript: '',
        finalScore: 0,
        fluencyScore: 0,
        grammarScore: 0,
        pronunciationScore: 0,
        mtiScore: 0,
        remark: 'Video analysis service is currently unavailable. Your video has been recorded but will be reviewed manually.',
        processingTime: 0,
        selectedScenario: selectedScenario || '',
        speechMetrics: {},
        grammarAnalysis: {},
        pronunciationAnalysis: {},
        mtiAnalysis: {},
        toneAnalysis: {},
        repetitionAnalysis: {}
      };
    }
  }

  /**
   * Transcribe video using Google Speech-to-Text API
   * Returns exact text without spell correction
   * @param {string} videoUrlOrPath - Full video URL or relative storage path
   * @returns {Promise<Object>} Transcription result with exact transcript
   */
  static async transcribeVideo(videoUrlOrPath) {
    try {
      // Convert relative path to full URL if needed
      let videoUrl = videoUrlOrPath;
      if (!videoUrlOrPath || typeof videoUrlOrPath !== 'string') {
        throw new Error(`Invalid video URL/path: ${videoUrlOrPath}`);
      }
      
      // CRITICAL: Reject blob URLs - videos must be uploaded to Firebase Storage first
      if (videoUrlOrPath.startsWith('blob:')) {
        const errorMsg = `Blob URL detected: ${videoUrlOrPath}. Videos must be uploaded to Firebase Storage before transcription. Please ensure the video is uploaded and use the Firebase Storage path (videoPath) instead.`;
        console.error('Transcription error:', errorMsg);
        return {
          success: false,
          error: errorMsg,
          transcript: ''
        };
      }
      
      if (!videoUrlOrPath.startsWith('http')) {
        // It's a relative path, add 'submissions/' prefix and convert to full Firebase Storage URL
        // videoPath format: "examCode/questionId/fileName"
        // Full path format: "submissions/examCode/questionId/fileName"
        const fullPath = videoUrlOrPath.startsWith('submissions/') 
          ? videoUrlOrPath 
          : `submissions/${videoUrlOrPath}`;
        
        try {
          const storageRef = firebase.storage().ref(fullPath);
          videoUrl = await storageRef.getDownloadURL();
        } catch (storageError) {
          console.error('Failed to get Firebase Storage URL:', storageError);
          throw new Error(`Failed to get Firebase Storage URL for path "${fullPath}": ${storageError.message}`);
        }
      }
      
      // Validate URL before sending to API
      if (!videoUrl || !videoUrl.startsWith('http')) {
        throw new Error(`Invalid video URL: ${videoUrl}`);
      }
      
      // Create AbortController for timeout handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 600000); // 10 minutes timeout

      const response = await fetch(`${ANALYSIS_API_BASE_URL}/api/transcribe-video`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ videoUrl }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Transcription failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        return {
          success: false,
          error: result.error || 'Transcription failed',
          transcript: ''
        };
      }

      // Return exact transcript without any modifications
      return {
        success: true,
        transcript: result.transcript || '', // Exact text as transcribed
        confidence: result.confidence || 0,
        wordCount: result.wordCount || 0,
        words: result.words || [],
        languageCode: result.languageCode || 'en-US',
        timestamp: result.timestamp || new Date().toISOString()
      };
    } catch (error) {
      if (error.name === 'AbortError') {
        return {
          success: false,
          error: 'Transcription timed out',
          transcript: ''
        };
      }
      
      console.error('Transcription error:', error);
      return {
        success: false,
        error: error.message || 'Transcription service unavailable',
        transcript: ''
      };
    }
  }

  /**
   * Check analysis status (for async processing)
   * @param {string} analysisId - Analysis identifier
   * @returns {Promise<Object>} Status and result if available
   */
  static async checkAnalysisStatus(analysisId) {
    // Deprecated: Status checking is no longer needed with Google Speech-to-Text
    // Transcription is synchronous and completes immediately
    console.warn('checkAnalysisStatus is deprecated. Transcription is now synchronous.');
    return {
      status: 'completed',
      message: 'Transcription is synchronous and completes immediately'
    };
  }

  /**
   * Validate video URL format
   * @param {string} url - Video URL to validate
   * @returns {boolean} True if valid
   */
  static validateVideoUrl(url) {
    if (!url || typeof url !== 'string') {
      return false;
    }

    // Check for Loom URLs
    const loomPattern = /^https?:\/\/(www\.)?loom\.com\/share\/[a-zA-Z0-9-]+/;
    if (loomPattern.test(url)) {
      return true;
    }

    // Check for Google Drive URLs
    const drivePattern = /^https?:\/\/drive\.google\.com\/(file\/d\/|open\?id=)[a-zA-Z0-9-_]+/;
    if (drivePattern.test(url)) {
      return true;
    }

    // Check for YouTube URLs (optional)
    const youtubePattern = /^https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[a-zA-Z0-9-_]+/;
    if (youtubePattern.test(url)) {
      return true;
    }

    return false;
  }

  /**
   * Format analysis results for display
   * @param {Object} analysisData - Raw analysis data from API
   * @returns {Object} Formatted results
   */
  static formatAnalysisResults(analysisData) {
    return {
      fluencyScore: analysisData.fluency_score || 0,
      grammarScore: analysisData.grammar_score || 0,
      pronunciationScore: analysisData.pronunciation_score || 0,
      mtiScore: analysisData.mti_score || 0,
      finalScore: analysisData.final_score || 0,
      remark: analysisData.remark || 'No remark',
      transcript: analysisData.transcript || 'No transcript available',
      processingTime: analysisData.processing_time || 0,
      analysisTimestamp: analysisData.analysis_timestamp || new Date().toISOString(),
      examCode: analysisData.exam_code || 'unknown',
      selectedScenario: analysisData.selected_scenario || ''
    };
  }

  /**
   * Get overall feedback based on scores
   * @param {Object} scores - Score object
   * @returns {string} Feedback message
   */
  static getOverallFeedback(scores) {
    const { fluencyScore, grammarScore, pronunciationScore, mtiScore, finalScore } = scores;
    
    if (finalScore >= 18) {
      return "Excellent performance! Your communication skills are outstanding.";
    } else if (finalScore >= 15) {
      return "Good performance! You have strong communication skills with room for improvement.";
    } else if (finalScore >= 12) {
      return "Fair performance! Focus on improving pronunciation and grammar.";
    } else if (finalScore >= 9) {
      return "Needs improvement! Consider working on fluency and pronunciation.";
    } else {
      return "Requires significant improvement! Focus on all aspects of communication.";
    }
  }
}

export default VideoAnalysisService; 